import React, { useEffect, useState } from 'react';
import { Button, Form } from 'antd';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListResource } from '../../components';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';
import { handleSubmit } from './utils/handleSubmit';
import { useMailSenderListParameters } from './utils/useMailSenderListParameters';
import { getResources } from './utils/getResources';
import { useGetEnums } from './utils/useGetEnums';
import { CreateUpdateExtraFilters } from './extraFilters/CreateUpdateExtraFilters';
import { collectionDispatch } from './utils/collectionDispatch';
import { TemplateDocumentButton } from '../../components/TemplateDocumentButton/TemplateDocumentButton';

/**
 * Component for creating or updating a mail sender.
 * Manages form submission and displays a list of mail senders.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.purpose - The purpose of the create/update operation.
 * @returns {JSX.Element} React element representing the component.
 */

export const MailSenderCreateUpdate = ({ purpose }) => {
  const [enums, setEnums] = useState({});
  const { type, sendingType, id } = useParams();

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  useGetEnums({ type, setEnums, purpose });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (purpose === 'edit') {
      (async () => {
        await getResources({
          dispatchAPI,
          type,
          id,
          setSelectedRowKeys,
          form,
          message
        });
      })();
    }
  }, [id]);

  // Keeps the recipients checked on the other pages of the table, and those
  // of an edited mailing that are not on the page shown.
  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (rowKeys) => {
      setSelectedRowKeys(rowKeys);
    }
  };

  const { resourceName, columns, extraQuery, resourceModelName } =
    useMailSenderListParameters({
      type,
      sendingType,
      enums
    });

  const blankTemplateConfig = collectionDispatch(type);

  return (
    <ListResource
      resourceName={resourceName}
      columns={columns}
      resourceModelName={resourceModelName}
      customActionColumn
      withCreateButton={false}
      extraQuery={extraQuery}
      customPathName={`mail-sender-${sendingType.toLowerCase()}/${purpose}/${id}`}
      customPageHeaderTitle={t('mail-sender.form-title', {
        sending_type: sendingType === 'POST' ? 'courriers' : 'mails',
        purpose: purpose === 'edit' ? 'modifier' : 'créer'
      })}
      extraFilters={
        <>
          {sendingType === 'EMAIL' ? (
            <CreateUpdateExtraFilters form={form} />
          ) : null}{' '}
          {blankTemplateConfig.blankUseCase ||
          blankTemplateConfig?.multi?.some((m) => m.blankUseCase) ? (
            <TemplateDocumentButton
              parameters={[
                {
                  collection:
                    blankTemplateConfig.blankCollection ||
                    blankTemplateConfig?.multi?.find((m) => m.blankCollection)
                      ?.blankCollection
                }
              ]}
              use_case={
                blankTemplateConfig.blankUseCase ||
                blankTemplateConfig?.multi?.find((m) => m.blankUseCase)
                  ?.blankUseCase
              }
              button_text="mail-sender.buttons.dl_neutral_template"
              buttonType="link"
              downloadName={`${t(`mail-sender.tags.${type}`)}_vierge`}
            />
          ) : null}
        </>
      }
      rowSelection={{
        type: 'checkbox',
        ...rowSelection
      }}
      bottom_children={
        <>
          <Button type="link" danger onClick={() => navigate(-1)}>
            {t('buttons.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSubmit({
                form,
                sendingType,
                purpose,
                dispatchAPI,
                id,
                type,
                selectedRowKeys,
                user,
                navigate,
                message,
                t
              })
            }
          >
            {t('buttons.confirm')}
          </Button>
        </>
      }
    />
  );
};

MailSenderCreateUpdate.propTypes = {
  purpose: PropTypes.string.isRequired
};
